import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RefreshCcw, RotateCcw, Send } from "lucide-react";
import AudioSourcePicker from "@/components/sunovoice/AudioSourcePicker";
import { useObjectUrl } from "@/components/sunovoice/useMicRecorder";
import { askNewPhrase, uploadVoiceAudio, verifySunoVoice } from "@/lib/sunoVoicesApi";
import { safeApiError } from "@/lib/safeErrorText";

/** Step 2 — Suno's phrase, shown big; sing it back (record or upload) and send it. */
export default function PhraseStep({ voice, onChanged }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(null);
  const url = useObjectUrl(file);
  const send = async () => {
    try {
      setBusy("Uploading…");
      const link = await uploadVoiceAudio(file);
      setBusy("Sending to ELPM…");
      onChanged(await verifySunoVoice(voice.id, link));
      toast.success("ELPM is building your voice", { description: "Usually a few minutes — you can leave this page." });
    } catch (e) { toast.error("Couldn't send your phrase", { description: safeApiError(e) }); setBusy(null); }
  };
  const again = async () => {
    setBusy("Asking for a new phrase…");
    try { onChanged(await askNewPhrase(voice.id)); setFile(null); } catch (e) { toast.error("Couldn't get a new phrase", { description: safeApiError(e) }); }
    setBusy(null);
  };
  return (
    <section data-testid="phrase-step" className="space-y-4 rounded-3xl border border-cyan-300/25 bg-[#0a0d1c]/85 p-6">
      {voice.error ? <p data-testid="phrase-error" className="flex items-start gap-2 rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-100"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{voice.error}</p> : null}
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300/80">Sing (or say) this phrase, in your normal voice</p>
      <blockquote data-testid="phrase-text" className="font-display text-2xl font-semibold leading-snug text-white sm:text-3xl">“{voice.phrase}”</blockquote>
      <p className="text-[12px] text-white/50">Use the same mic, no music behind you, every word clear. This proves to ELPM that the voice is yours.</p>
      {file ? (
        <div data-testid="phrase-take" className="space-y-3 rounded-2xl border border-white/[0.08] bg-black/30 p-4">
          <audio controls src={url || undefined} data-testid="phrase-take-audio" className="w-full" />
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid="phrase-send" onClick={send} disabled={Boolean(busy)} className="inline-flex items-center gap-2 rounded-full bg-cyan-300 px-5 py-2.5 text-[13px] font-semibold text-black disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {busy || "Send to ELPM"}
            </button>
            <button type="button" data-testid="phrase-redo" onClick={() => setFile(null)} disabled={Boolean(busy)} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2.5 text-[13px] hover:bg-white/[0.06] disabled:opacity-40"><RotateCcw className="h-4 w-4" /> Record again</button>
          </div>
        </div>
      ) : <AudioSourcePicker testId="phrase-source" maxSec={60} onFile={setFile} hint="Takes about 5–10 seconds." />}
      <button type="button" data-testid="phrase-new" onClick={again} disabled={Boolean(busy)} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/50 hover:text-white disabled:opacity-40">
        <RefreshCcw className="h-3 w-3" /> Ask for a different phrase
      </button>
    </section>
  );
}
